'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ActionIcon, Button, Group, Modal, Stack, Textarea, TextInput, Title } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';
import { Dropzone } from '../EditorText/Dropzone';

export function FooterEditorModal() {
  const [opened, setOpened] = useState(false);
  const dictionary = useDictionary();
  const [footer, setFooter] = useState(dictionary.footer);
  const [logoUrl, setLogoUrl] = useState(dictionary.logoUrl);
  const pathname = usePathname();
  const locale = pathname.startsWith('/en') ? 'en' : 'fr';

  const updateField = (field: string, value: string) => {
    setFooter({ ...footer, [field]: value });
  };

  const updateSocialLink = (platform: string, value: string) => {
    setFooter({ ...footer, [platform]: value });
  };

  const updateGroupTitle = (index: number, title: string) => {
    const updated = [...footer.links];
    updated[index].title = title;
    setFooter({ ...footer, links: updated });
  };

  const updateLink = (
    groupIndex: number,
    linkIndex: number,
    field: 'label' | 'link',
    value: string
  ) => {
    const updated = [...footer.links];
    updated[groupIndex].links[linkIndex][field] = value;
    setFooter({ ...footer, links: updated });
  };

  const addGroup = () => {
    setFooter({
      ...footer,
      links: [...footer.links, { title: '', links: [] }],
    });
  };

  const removeGroup = (index: number) => {
    const updated = [...footer.links];
    updated.splice(index, 1);
    setFooter({ ...footer, links: updated });
  };

  const addLink = (groupIndex: number) => {
    const updated = [...footer.links];
    updated[groupIndex].links.push({ label: '', link: '' });
    setFooter({ ...footer, links: updated });
  };

  const removeLink = (groupIndex: number, linkIndex: number) => {
    const updated = [...footer.links];
    updated[groupIndex].links.splice(linkIndex, 1);
    setFooter({ ...footer, links: updated });
  };

  const handleSave = async () => {
    const res = await fetch('/api/update-footer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ footer, logoUrl, locale }),
    });

    if (res.ok) {
      addDeploymentToast('Déploiement du footer en cours. Optimisation des fichiers...');
      setOpened(false);
    } else {
      // eslint-disable-next-line no-alert
      alert('Erreur lors de la sauvegarde');
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpened(true)}
        size="xs"
        style={{ position: 'absolute', right: '10px' }}
        color="black"
      >
        ✏️ Modifier le footer
      </Button>

      <Modal opened={opened} onClose={() => setOpened(false)} title="Modifier le footer" size="xl">
        <Stack>
          <Title order={4}>Logo</Title>
          <Dropzone imageUrl={logoUrl} setImageUrl={setLogoUrl} />

          <Title order={4}>Description</Title>
          <Textarea
            value={footer.description}
            onChange={(e) => updateField('description', e.currentTarget.value)}
          />

          <Title order={4}>Copyright</Title>
          <TextInput
            value={footer.copyright}
            onChange={(e) => updateField('copyright', e.currentTarget.value)}
          />

          <Title order={4}>Réseaux sociaux</Title>
          <TextInput
            label="Instagram"
            value={footer.instagram}
            onChange={(e) => updateSocialLink('instagram', e.currentTarget.value)}
          />
          <TextInput
            label="Facebook"
            value={footer.facebook}
            onChange={(e) => updateSocialLink('facebook', e.currentTarget.value)}
          />
          <TextInput
            label="Twitter"
            value={footer.twitter}
            onChange={(e) => updateSocialLink('twitter', e.currentTarget.value)}
          />
          <TextInput
            label="TikTok"
            value={footer.tiktok}
            onChange={(e) => updateSocialLink('tiktok', e.currentTarget.value)}
          />

          <Title order={4}>Liens</Title>
          {footer.links.map(
            (group: { title: string; links: { label: string; link: string }[] }, i: number) => (
              <Stack key={i}>
                <Group>
                  <TextInput
                    label="Titre du groupe"
                    value={group.title}
                    onChange={(e) => updateGroupTitle(i, e.currentTarget.value)}
                  />
                  <ActionIcon onClick={() => removeGroup(i)} color="red">
                    <IconTrash />
                  </ActionIcon>
                </Group>
                {group.links.map((link: { label: string; link: string }, j: number) => (
                  <Group key={j}>
                    <TextInput
                      label="Label"
                      value={link.label}
                      onChange={(e) => updateLink(i, j, 'label', e.currentTarget.value)}
                    />
                    <TextInput
                      label="Lien"
                      value={link.link}
                      onChange={(e) => updateLink(i, j, 'link', e.currentTarget.value)}
                    />
                    <ActionIcon onClick={() => removeLink(i, j)} color="red">
                      <IconTrash />
                    </ActionIcon>
                  </Group>
                ))}
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => addLink(i)}
                  leftSection={<IconPlus size={16} />}
                >
                  Ajouter un lien
                </Button>
              </Stack>
            )
          )}

          <Button leftSection={<IconPlus size={16} />} onClick={addGroup} variant="light">
            Ajouter un groupe de liens
          </Button>

          <Button fullWidth mt="md" onClick={handleSave}>
            💾 Enregistrer
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
